import type { ReactNode } from 'react';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';

interface StatProps {
  label: ReactNode;
  value: ReactNode;
  icon?: ReactNode;
  /** Small line under the value (e.g. "20 / 60 room-nights"). */
  sub?: ReactNode;
  /** Percent change vs previous period. Positive = green, negative = red. */
  deltaPct?: number | null;
  /** Some metrics are better when they go down (costs, waste). */
  invertDelta?: boolean;
  /** e.g. "vs yesterday" — rendered after the delta pill. */
  deltaLabel?: ReactNode;
}

export function Stats({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={['ui-stats', className].filter(Boolean).join(' ')}>{children}</div>;
}

export function Stat({ label, value, icon, sub, deltaPct, invertDelta, deltaLabel }: StatProps) {
  let delta: ReactNode = null;
  if (deltaPct !== undefined && deltaPct !== null && Number.isFinite(deltaPct)) {
    const good = invertDelta ? deltaPct < 0 : deltaPct > 0;
    const flat = Math.abs(deltaPct) < 0.05;
    const cls = flat ? 'ui-stat-delta--flat' : good ? 'ui-stat-delta--up' : 'ui-stat-delta--down';
    const Icon = flat ? Minus : deltaPct > 0 ? ArrowUpRight : ArrowDownRight;
    delta = (
      <div className="ui-stat-delta-row">
        <span className={`ui-stat-delta ${cls}`}>
          <Icon />
          {Math.abs(deltaPct).toFixed(1)}%
        </span>
        {deltaLabel && <span className="ui-stat-delta-label">{deltaLabel}</span>}
      </div>
    );
  }
  return (
    <div className="ui-stat">
      <div className="ui-stat-label">
        {icon}
        <span>{label}</span>
      </div>
      <div className="ui-stat-value">{value}</div>
      {sub && <div className="ui-stat-sub">{sub}</div>}
      {delta}
    </div>
  );
}
